import { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import { Grid, SvgIcon } from "@mui/material";
//@ts-expect-error Striim-ui missing type declarations
import { StriimTypography, StriimPagination, StriimCheckbox } from "@striim/striim-ui";

import { Tables } from "../../schema-table";
import SearchList from "app/images/SearchList.svg";
import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";

const PAGE_SIZE = 15;

const styles = {
    searchListIcon: {
        width: 150,
        height: 140
    }
};

interface TablesListProps {
    schemaName: string;
    tables?: Tables[];
    onTableSelection: (tableName: string, checked: boolean) => void;
    searchInput: string;
    schemaType: string;
    entityType: string;
    compact?: boolean;
}

export interface TablesListHandle {
    resetPage: () => void;
}

const TablesList = forwardRef<TablesListHandle, TablesListProps>(
    ({ schemaName, tables, onTableSelection, searchInput, schemaType, entityType, compact = false }, ref) => {
        const [page, setPage] = useState(1);

        useImperativeHandle(ref, () => ({ resetPage: () => setPage(1) }), []);

        useEffect(() => {
            setPage(1);
        }, [schemaName, searchInput]);

        const pageCount = useMemo(() => Math.ceil((tables?.length || 0) / PAGE_SIZE), [tables?.length]);

        const paginatedTables = useMemo(() => {
            if (!tables?.length) return [];
            const start = (page - 1) * PAGE_SIZE;
            return tables.slice(start, start + PAGE_SIZE);
        }, [tables, page]);

        const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
            setPage(value);
        };

        return (
            <Grid display="flex" flexDirection="column" width={"100%"} flex={1}>
                <Grid
                    display="flex"
                    flexDirection="column"
                    width={"100%"}
                    p={compact ? 1.5 : 2}
                    {...(compact
                        ? {
                              overflow: "scroll",
                              height: "280px"
                          }
                        : { flex: 1 })}
                    gap={1.5}
                    overflow={"auto"}
                    data-test-id="select-tables-table"
                >
                    {paginatedTables.length ? (
                        paginatedTables.map(table => (
                            <Grid
                                key={schemaName + table.name}
                                item
                                display={"flex"}
                                alignItems={"center"}
                                gap={1}
                                minWidth={0}
                                overflow={"hidden"}
                                flexShrink={0}
                            >
                                <StriimCheckbox
                                    checked={table.isSelected}
                                    onChange={event => onTableSelection(table.name, event.target.checked)}
                                    data-test-id={`table-${table.name}-checkbox`}
                                />
                                <EllipsisText
                                    text={table.name}
                                    sx={{
                                        fontSize: "14px",
                                        color: "greyscale.700",
                                        fontWeight: 400,
                                        fontFamily: "Inter"
                                    }}
                                />
                            </Grid>
                        ))
                    ) : (
                        <Grid
                            container
                            justifyContent={"center"}
                            alignItems={"center"}
                            flexDirection={"column"}
                            gap={2}
                            mt={8}
                        >
                            {!!searchInput?.length ? (
                                <>
                                    <SvgIcon component={SearchList} sx={styles.searchListIcon} />
                                    <Grid
                                        item
                                        display={"flex"}
                                        flexDirection={"column"}
                                        alignItems={"center"}
                                        data-test-id="empty-tables-search"
                                    >
                                        <StriimTypography variant="h4" color="primary.900">
                                            No {entityType.toLowerCase()}s matching "{searchInput}" in{" "}
                                            {schemaType.toLowerCase()}: {schemaName}
                                        </StriimTypography>
                                        <StriimTypography variant="body4" color="greyscale.700">
                                            Try checking for typos or search using another keyword
                                        </StriimTypography>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <SvgIcon component={SearchList} sx={styles.searchListIcon} />
                                    <StriimTypography variant="body3" color="greyscale.700">
                                        No {entityType.toLowerCase()}s found in {schemaType.toLowerCase()}: {schemaName}
                                    </StriimTypography>
                                </>
                            )}
                        </Grid>
                    )}
                </Grid>
                <Grid container>
                    <StriimPagination shape="rounded" count={pageCount} page={page} onChange={handlePageChange} />
                </Grid>
            </Grid>
        );
    }
);

export default TablesList;
